import React, { useEffect } from 'react'
import { useSelector, useDispatch, batch } from 'react-redux'
import { Skeleton, Typography } from '@chilecompra/react-kit/components'
import {
  PUBLIC_URL,
  REACT_APP_FEATURE_FLAG_SUMMARY_SECTION_AVERAGE_DAYS,
  REACT_APP_FEATURE_FLAG_SUMMARY_SECTION_CLAIMS,
  REACT_APP_FEATURE_FLAG_SUMMARY_SECTION_PURCHASES
} from '../../config/settings/environment'
import { GESTION_TITLE } from '../../config/settings/constant'
import { formatAmount } from '../../modules/utils/formatters'
import { onGetAverageBehaviorDataThunk } from '../BehaviorSection/BehaviorSection.actions'
import { onGetTotalPurchaseOrdersThunk } from '../PurchaseOrdersSection/PurchaseOrdersSection.actions'
import { onGetAveragePayThunk, onGetTotalPurchasesThunk } from './SummarySection.actions'
import { onGetClaimInformationChartThunk } from '../ClaimsReceivedSection/ClaimsReceivedSection.actions'
import { Box, CustomCard, QuestionIcon, Info, Separator, Tooltip } from './SummarySection.styles'
import { setStringMonth } from '../BehaviorSection/BehaviorSection.utils'

/**
 * The Summary Section container.
 */
const SummarySection = () => {
  const dispatch = useDispatch()
  const { error: buyerError, dni } = useSelector(state => state.basicBuyerInformation)
  const { error, loading, rangeDate, totalPurchaseAmount } = useSelector(state => state.totalPurchases)
  const {
    averageBehaviorIndicatorLoading: loadingBehavior,
    averageBehaviorIndicatorError: errorBehavior,
    averageBehaviorIndicator: indicator,
    averageBehaviorYear,
    averageBehaviorMonth
  } = useSelector(state => state.behaviorSection)

  const {
    error: claimError,
    loading: loadingClaim,
    totalClaim,
    rangeDate: rangeDateClaim
  } = useSelector(state => state.claimInformation)
  const {
    error: errorAvg,
    loading: loadingAvg,
    days,
    sigfe,
    rangeDate: rangeDateAvg
  } = useSelector(state => state.averagePay)
  const { error: errorOrders, loading: loadingOrders, totalOrder } = useSelector(state => state.totalPurchaseOrders)
  const hasClaims = totalClaim >= 0 && totalOrder > 0

  const stringMonth = setStringMonth(averageBehaviorYear, averageBehaviorMonth)

  useEffect(() => {
    if (!dni) return
    batch(() => {
      dispatch(onGetAveragePayThunk(dni))
      dispatch(onGetClaimInformationChartThunk(dni))
      dispatch(onGetTotalPurchasesThunk(dni))
      dispatch(onGetTotalPurchaseOrdersThunk(dni))
      dispatch(onGetAverageBehaviorDataThunk({ dni }))
    })
  }, [dni, dispatch])

  return buyerError ? null : (
    <CustomCard padding="14px 20px 20px 16px">
      {(REACT_APP_FEATURE_FLAG_SUMMARY_SECTION_PURCHASES === true ||
        REACT_APP_FEATURE_FLAG_SUMMARY_SECTION_PURCHASES === 'true') && (
        <>
          <Box padding="10px 0">
            <Info>
              <Typography
                color="black3"
                align="left"
                fontWeight="bold"
                variant="caption"
                fontSize="14px"
                lineHeight="16px"
                tabIndex="0"
                padding="0 0 15px"
              >
                Compras en Mercado Público
                <Tooltip
                  content="La información de compras en Mercado Público corresponde al monto bruto (monto neto + impuestos)."
                  placement="top"
                  tabIndex="0"
                >
                  <QuestionIcon src={`${PUBLIC_URL}/assets/images/question-icon.svg`} alt="Ayuda" />
                </Tooltip>
              </Typography>
            </Info>
            {loading && (
              <>
                <Skeleton height="30px" variant="rectangular" width="100%" />
                <Skeleton height="30px" variant="rectangular" width="100%" />
              </>
            )}
            {!error && !loading && (
              <>
                <Typography
                  align="left"
                  fontWeight="bold"
                  variant="body1"
                  fontSize="27px"
                  color="black1"
                  lineHeight="31px"
                  tabIndex="0"
                >
                  {totalPurchaseAmount === -1 ? 'Sin información' : '$ ' + formatAmount(totalPurchaseAmount)}
                </Typography>
                <Typography
                  align="left"
                  fontWeight="regular"
                  variant="body1"
                  fontSize="14px"
                  color="black1"
                  lineHeight="18px"
                  tabIndex="0"
                >
                  {totalPurchaseAmount === -1
                    ? `No ha realizado compras entre ${rangeDate.initialMonth} ${rangeDate.initialYear} y ${rangeDate.finalMonth} ${rangeDate.finalYear}`
                    : `Periodo consultado: ${rangeDate.initialMonth} ${rangeDate.initialYear} a
              ${rangeDate.finalMonth} ${rangeDate.finalYear}. Impuestos incluidos.`}
                </Typography>
              </>
            )}
            {error && !loading && (
              <>
                <Typography
                  align="left"
                  fontWeight="bold"
                  variant="body1"
                  fontSize="27px"
                  color="black1"
                  lineHeight="31px"
                  tabindex="0"
                >
                  Sin información
                </Typography>
                <Typography
                  align="left"
                  fontWeight="regular"
                  variant="subtitle"
                  fontSize="14px"
                  color="black3"
                  lineHeight="18px"
                  tabIndex="0"
                >
                  Servicio temporalmente no disponible.
                </Typography>
              </>
            )}
          </Box>
          <Separator />
        </>
      )}

      <>
        <Box padding="10px 0">
          <Info>
            <Typography
              color="black3"
              align="left"
              fontWeight="bold"
              variant="caption"
              fontSize="14px"
              lineHeight="16px"
              tabIndex="0"
              padding="0 0 15px"
            >
              {GESTION_TITLE}
            </Typography>
          </Info>
          {(loadingBehavior || loading) && (
            <>
              <Skeleton height="30px" variant="rectangular" width="100%" />
              <Skeleton height="30px" variant="rectangular" width="100%" />
            </>
          )}
          {!errorBehavior && !loadingBehavior && !loading && typeof indicator === 'number' && (
            <>
              <Typography
                align="left"
                fontWeight="bold"
                variant="body1"
                fontSize="27px"
                color="black1"
                lineHeight="31px"
                tabIndex="0"
                display="flex"
                alignItems="baseline"
              >
                {indicator?.toFixed(2).replace('.', ',') || 0}
                <Typography variant="body1" fontSize="20px" fontWeight="bold" color="black1">
                  /5
                </Typography>
              </Typography>
              <Typography
                align="left"
                fontWeight="regular"
                variant="body1"
                fontSize="14px"
                color="black1"
                lineHeight="18px"
                tabIndex="0"
              >
                Nota calculada en base al puntaje de las cinco dimensiones de Gestión de compras durante el mes de{' '}
                {stringMonth} {averageBehaviorYear}.
              </Typography>
            </>
          )}
          {errorBehavior && !loadingBehavior && !loading && (
            <>
              <Typography
                align="left"
                fontWeight="bold"
                variant="body1"
                fontSize="27px"
                color="black1"
                lineHeight="31px"
                tabindex="0"
              >
                Sin información
              </Typography>
              <Typography
                align="left"
                fontWeight="regular"
                variant="subtitle"
                fontSize="14px"
                color="black3"
                lineHeight="18px"
                tabIndex="0"
              >
                Servicio temporalmente no disponible.
              </Typography>
            </>
          )}
        </Box>
        <Separator />
      </>

      {(REACT_APP_FEATURE_FLAG_SUMMARY_SECTION_CLAIMS === true ||
        REACT_APP_FEATURE_FLAG_SUMMARY_SECTION_CLAIMS === 'true') && (
        <>
          <Box padding="10px 0">
            <Typography
              color="black3"
              align="left"
              fontWeight="bold"
              variant="caption"
              fontSize="14px"
              lineHeight="16px"
              tabIndex="0"
              padding="0 0 15px"
            >
              Reclamos
            </Typography>
            {loadingClaim && (
              <>
                <Skeleton height="30px" variant="rectangular" width="100%" />
                <Skeleton height="30px" variant="rectangular" width="100%" />
              </>
            )}

            {!claimError && !loadingClaim && !errorOrders && !loadingOrders && (
              <>
                <Typography
                  align="left"
                  fontWeight="bold"
                  variant="body1"
                  fontSize="27px"
                  color="black1"
                  lineHeight="31px"
                  tabIndex="0"
                >
                  {!hasClaims ? 'Sin información' : formatAmount(totalClaim) + ' reclamos'}
                </Typography>
                <Typography
                  align="left"
                  fontWeight="regular"
                  variant="body1"
                  fontSize="14px"
                  color="black1"
                  lineHeight="18px"
                  tabIndex="0"
                >
                  {!hasClaims
                    ? ` Sin información entre ${rangeDateClaim.initialMonth} ${rangeDateClaim.initialYear} y ${rangeDateClaim.finalMonth} ${rangeDateClaim.finalYear}`
                    : ` entre ${rangeDateClaim.initialMonth} 
                ${rangeDateClaim.initialYear} a
              ${rangeDateClaim.finalMonth} ${rangeDateClaim.finalYear}`}
                </Typography>
              </>
            )}
            {claimError && !loadingClaim && (
              <>
                <Typography
                  align="left"
                  fontWeight="bold"
                  variant="body1"
                  fontSize="27px"
                  color="black1"
                  lineHeight="31px"
                  tabIndex="0"
                >
                  Sin información
                </Typography>
                <Typography
                  align="left"
                  fontWeight="regular"
                  variant="subtitle"
                  fontSize="14px"
                  color="black3"
                  lineHeight="18px"
                  tabIndex="0"
                >
                  Servicio temporalmente no disponible.
                </Typography>
              </>
            )}
          </Box>
          <Separator />
        </>
      )}

      {(REACT_APP_FEATURE_FLAG_SUMMARY_SECTION_AVERAGE_DAYS === true ||
        REACT_APP_FEATURE_FLAG_SUMMARY_SECTION_AVERAGE_DAYS === 'true') && (
        <Box padding="10px 0">
          <Typography
            color="black3"
            align="left"
            fontWeight="bold"
            variant="caption"
            fontSize="14px"
            lineHeight="16px"
            tabIndex="0"
            padding="0 0 15px"
          >
            Promedio días de pago
            {sigfe === false && (
              <Tooltip
                content="La información de días de pago solamente se encuentra disponible para aquellas instituciones que utilicen el Sistema de Información para la Gestión Financiera del Estado (SIGFE)"
                placement="top"
                tabIndex="0"
              >
                <QuestionIcon src={`${PUBLIC_URL}/assets/images/question-icon.svg`} alt="Ayuda" />
              </Tooltip>
            )}
          </Typography>
          {loadingAvg && (
            <>
              <Skeleton height="30px" variant="rectangular" width="100%" />
              <Skeleton height="70px" variant="rectangular" width="100%" />
            </>
          )}

          {!errorAvg && !loadingAvg && (
            <>
              <Typography
                align="left"
                fontWeight="bold"
                variant="body1"
                fontSize="27px"
                color="black1"
                lineHeight="31px"
                tabIndex="0"
              >
                {days === -1 || sigfe === false ? 'Sin información' : `${days} días`}
              </Typography>
              <Typography
                align="left"
                fontWeight="regular"
                variant="body1"
                fontSize="14px"
                color="black1"
                lineHeight="18px"
                tabIndex="0"
              >
                {sigfe === false ? (
                  `Este organismo comprador no utiliza el Sistema de Información para la Gestión Financiera del Estado (SIGFE)`
                ) : (
                  <>
                    Entre {rangeDateAvg.initialMonth} {rangeDateAvg.initialYear} y {rangeDateAvg.finalMonth}{' '}
                    {rangeDateAvg.finalYear}.
                    <br />
                    Información obtenida desde el Sistema de Información para la Gestión Financiera del Estado (SIGFE).
                  </>
                )}
              </Typography>
            </>
          )}
          {errorAvg && !loadingAvg && (
            <>
              <Typography
                align="left"
                fontWeight="bold"
                variant="body1"
                fontSize="27px"
                color="black1"
                lineHeight="31px"
                tabIndex="0"
              >
                Sin información
              </Typography>
              <Typography
                align="left"
                fontWeight="regular"
                variant="subtitle"
                fontSize="14px"
                color="black3"
                lineHeight="18px"
                tabIndex="0"
              >
                Servicio temporalmente no disponible.
              </Typography>
            </>
          )}
        </Box>
      )}
    </CustomCard>
  )
}

export default SummarySection
